import { clsx } from 'clsx';

// ● Full access  ◑ Partial / restricted  ○ No access
type Access = 'full' | 'partial' | 'none';

interface Cell {
  access: Access;
  note?: string;   // shown as tooltip / footnote marker
}

type Role =
  | 'hospital_admin' | 'doctor' | 'nurse' | 'receptionist'
  | 'lab_technician' | 'pharmacist' | 'billing_staff' | 'accountant' | 'radiologist';

interface PermissionRow {
  group: string;
  label: string;
  cells: Record<Role, Cell>;
}

const ROLES: { key: Role; label: string }[] = [
  { key: 'hospital_admin', label: 'Hospital Admin' },
  { key: 'doctor',         label: 'Doctor' },
  { key: 'nurse',          label: 'Nurse' },
  { key: 'receptionist',   label: 'Receptionist' },
  { key: 'lab_technician', label: 'Lab Tech' },
  { key: 'pharmacist',     label: 'Pharmacist' },
  { key: 'billing_staff',  label: 'Billing Staff' },
  { key: 'accountant',     label: 'Accountant' },
  { key: 'radiologist',    label: 'Radiologist' },
];

const f = (note?: string): Cell => ({ access: 'full',    note });
const p = (note?: string): Cell => ({ access: 'partial', note });
const n = ():               Cell => ({ access: 'none' });

const MATRIX: PermissionRow[] = [
  // ── User Management ──────────────────────────────────────────────────
  {
    group: 'User Management',
    label: 'User Management',
    cells: {
      hospital_admin: f(),
      doctor:         n(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  // ── Patient Management ───────────────────────────────────────────────
  {
    group: 'Patient Management',
    label: 'Patient Registration',
    cells: {
      hospital_admin: f(),
      doctor:         f(),
      nurse:          f(),
      receptionist:   f(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  {
    group: 'Patient Management',
    label: 'Full PII Access',
    cells: {
      hospital_admin: f(),
      doctor:         f(),
      nurse:          f(),
      receptionist:   f(),
      lab_technician: p('Name + ID only'),
      pharmacist:     p('Name + ID only'),
      billing_staff:  p('Name + ID only'),
      accountant:     n(),
      radiologist:    p('Basic info only'),
    },
  },
  // ── OPD / Encounters ─────────────────────────────────────────────────
  {
    group: 'OPD / Encounters',
    label: 'OPD Queue',
    cells: {
      hospital_admin: p('View only'),
      doctor:         p('Own patients only'),
      nurse:          f(),
      receptionist:   f(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  {
    group: 'OPD / Encounters',
    label: 'Clinical Notes (SOAP)',
    cells: {
      hospital_admin: n(),
      doctor:         f(),
      nurse:          p('Read only'),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  {
    group: 'OPD / Encounters',
    label: 'Vitals',
    cells: {
      hospital_admin: n(),
      doctor:         f(),
      nurse:          f(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  // ── Prescriptions ────────────────────────────────────────────────────
  {
    group: 'Prescriptions',
    label: 'Prescriptions',
    cells: {
      hospital_admin: n(),
      doctor:         f(),
      nurse:          p('Read only'),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     p('Dispense only'),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  // ── Scheduling ───────────────────────────────────────────────────────
  {
    group: 'Scheduling',
    label: 'OPD Scheduling',
    cells: {
      hospital_admin: p('View only'),
      doctor:         p('Own slots only'),
      nurse:          n(),
      receptionist:   f(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  // ── IPD ──────────────────────────────────────────────────────────────
  {
    group: 'IPD',
    label: 'Admissions / Beds',
    cells: {
      hospital_admin: p('View only'),
      doctor:         f(),
      nurse:          p('View only'),
      receptionist:   p('View only'),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  p('View only'),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  // ── Lab ──────────────────────────────────────────────────────────────
  {
    group: 'Lab / Pathology',
    label: 'Lab Orders',
    cells: {
      hospital_admin: p('View only'),
      doctor:         f(),
      nurse:          p('View only'),
      receptionist:   n(),
      lab_technician: f('Full + report upload'),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    p('View only'),
    },
  },
  // ── Radiology ────────────────────────────────────────────────────────
  {
    group: 'Radiology',
    label: 'Radiology',
    cells: {
      hospital_admin: n(),
      doctor:         f(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    f('Full + report upload'),
    },
  },
  // ── Billing ──────────────────────────────────────────────────────────
  {
    group: 'Billing',
    label: 'Billing & Invoicing',
    cells: {
      hospital_admin: p('View only'),
      doctor:         n(),
      nurse:          n(),
      receptionist:   p('View only'),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  f('Full + discount'),
      accountant:     p('View only'),
      radiologist:    n(),
    },
  },
  {
    group: 'Billing',
    label: 'TPA / Insurance',
    cells: {
      hospital_admin: f(),
      doctor:         n(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  f(),
      accountant:     p('View only'),
      radiologist:    n(),
    },
  },
  // ── Finance ──────────────────────────────────────────────────────────
  {
    group: 'Finance',
    label: 'Financial Reports',
    cells: {
      hospital_admin: f(),
      doctor:         n(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  f(),
      accountant:     f(),
      radiologist:    n(),
    },
  },
  // ── Administration ───────────────────────────────────────────────────
  {
    group: 'Administration',
    label: 'Hospital Settings',
    cells: {
      hospital_admin: f(),
      doctor:         n(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  {
    group: 'Administration',
    label: 'Audit Logs',
    cells: {
      hospital_admin: p('Own tenant only'),
      doctor:         n(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
  {
    group: 'Administration',
    label: 'Consent Management',
    cells: {
      hospital_admin: f(),
      doctor:         n(),
      nurse:          n(),
      receptionist:   n(),
      lab_technician: n(),
      pharmacist:     n(),
      billing_staff:  n(),
      accountant:     n(),
      radiologist:    n(),
    },
  },
];

function AccessBadge({ cell }: { cell: Cell }) {
  if (cell.access === 'full') {
    return (
      <span title={cell.note}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md
                   bg-clinical-success/10 text-clinical-success text-base font-bold select-none">
        ●
      </span>
    );
  }
  if (cell.access === 'partial') {
    return (
      <span title={cell.note}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md
                   bg-orange/10 text-orange text-base font-bold select-none cursor-help">
        ◑
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md
                     text-gray-200 text-base select-none">
      ○
    </span>
  );
}

function groupRows(rows: PermissionRow[]) {
  const map = new Map<string, PermissionRow[]>();
  for (const row of rows) {
    if (!map.has(row.group)) map.set(row.group, []);
    map.get(row.group)!.push(row);
  }
  return map;
}

export default function RbacMatrix() {
  const grouped = groupRows(MATRIX);

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-navy">RBAC Permissions Matrix</h1>
        <p className="text-gray-400 text-sm mt-1">
          Enforced at API middleware layer per{' '}
          <span className="font-medium text-navy">DPDP Act 2023, Section 6</span>{' '}
          (data minimization). All ◑ partial restrictions are applied server-side, not just in the UI.
        </p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm">
        <span className="flex items-center gap-1.5 text-clinical-success font-medium">
          <span className="text-base leading-none">●</span> Full access
        </span>
        <span className="flex items-center gap-1.5 text-orange font-medium">
          <span className="text-base leading-none">◑</span> Partial / restricted
          <span className="text-gray-400 font-normal">(hover for details)</span>
        </span>
        <span className="flex items-center gap-1.5 text-gray-300 font-medium">
          <span className="text-base leading-none">○</span> No access
        </span>
      </div>

      {/* Matrix table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase
                             tracking-wide min-w-[180px] sticky left-0 bg-gray-50 z-10 border-r border-gray-200">
                Permission Area
              </th>
              {ROLES.map(r => (
                <th key={r.key}
                  className="px-2 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide
                             text-center min-w-[88px]">
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from(grouped.entries()).map(([group, rows]) =>
              rows.map((row, i) => (
                <tr key={`${group}-${row.label}`}
                  className={clsx(
                    'border-b border-gray-100 last:border-0',
                    i === 0 && 'border-t-2 border-t-gray-100',
                  )}>
                  <td className={clsx(
                    'px-4 py-2.5 sticky left-0 bg-white z-10 border-r border-gray-100',
                  )}>
                    {i === 0 && (
                      <div className="text-2xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">
                        {group}
                      </div>
                    )}
                    <div className="text-sm text-navy font-medium">{row.label}</div>
                  </td>
                  {ROLES.map(r => (
                    <td key={r.key} className="px-2 py-2.5 text-center">
                      <AccessBadge cell={row.cells[r.key]}/>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footnotes */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm space-y-1.5">
        <p className="font-semibold text-navy text-xs uppercase tracking-wide mb-2">
          Partial Access Details
        </p>
        {[
          ['Doctor – OPD Queue', 'API auto-scopes encounters to the requesting doctor\'s own patient list (appointments:own).'],
          ['Doctor – OPD Scheduling', 'Doctor can only view and manage their own appointment slots.'],
          ['Nurse – Clinical Notes', 'Read-only access to SOAP notes; cannot create or modify diagnoses.'],
          ['Nurse – Prescriptions', 'Can view prescriptions but cannot issue new ones.'],
          ['Pharmacist – Prescriptions', 'Dispense-only: can mark as dispensed but cannot write new prescriptions.'],
          ['Lab Tech / Pharmacist / Billing – Patient', 'patients:read_limited — name and ID only. No Aadhaar, mobile, or clinical data.'],
          ['Hospital Admin – Billing', 'View-only access to invoices and billing records; cannot post charges.'],
          ['Hospital Admin – Audit Logs', 'Scoped to own tenant by architecture; cannot see other tenants\' logs.'],
          ['Radiologist – Lab Orders', 'View-only to correlate imaging with lab results.'],
        ].map(([title, desc]) => (
          <div key={title} className="flex gap-2 text-xs text-gray-600">
            <span className="font-medium text-navy whitespace-nowrap">{title}:</span>
            <span>{desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
